import React, { useState, useEffect, useRef } from 'react';
import { FaFilter, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { fetchSupplyCategories } from '../services/apiService';
import { useLanguage } from '../contexts/LanguageContext';

const SupplyCategoryFilter = ({ onCategorySelect, onClear }) => {
  const { t } = useLanguage();
  const [supplyCategories, setSupplyCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const loadSupplyCategories = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetchSupplyCategories();
        // Handle the specific data structure with codes array
        const categories = response.data?.codes || response.codes || [];
        setSupplyCategories(categories);
      } catch (error) {
        console.error('Error loading supply categories:', error);
        setError(t('filters.supplyCategory.error'));
        setSupplyCategories([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadSupplyCategories();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    if (category) {
      onCategorySelect(category);
    } else {
      onClear();
    }
  };

  const handleClear = () => {
    setSelectedCategory('');
    onClear();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="flex items-center gap-3">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="inline-flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
        >
          <FaFilter className="text-gray-400" />
          {t('filters.supplyCategory.title')}
          {isOpen ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
        </button>

        {selectedCategory && (
          <button
            onClick={handleClear}
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors duration-200"
          >
            {t('filters.supplyCategory.clearFilter')}
          </button>
        )}
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-[99999]" style={{ transform: 'translateZ(0)' }}>
          <div className="p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('filters.supplyCategory.modalTitle')}</h3>
            
            {error && (
              <div className="text-red-600 text-sm mb-3">
                {error}
              </div>
            )}

            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                <span className="ml-2 text-sm text-gray-500">{t('filters.supplyCategory.loading')}</span>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Category Dropdown */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('filters.supplyCategory.label')}
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={e => handleCategoryChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">{t('filters.supplyCategory.placeholder')}</option>
                    {supplyCategories.length > 0 ? (
                      supplyCategories.map((category) => (
                        <option key={category.code} value={category.code}>
                          {t('filters.supplyCategory.categoryOption', { code: category.code, count: category.count })}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>{t('filters.supplyCategory.noCategories')}</option>
                    )}
                  </select>
                </div>

                {/* Selected Filter Display */}
                {selectedCategory && (
                  <div className="mt-3 p-2 bg-indigo-50 border border-indigo-200 rounded-md">
                    <p className="text-xs text-indigo-800">
                      <span className="font-medium">{t('filters.supplyCategory.selected')}</span>
                      <span className="ml-1">
                        {t('filters.supplyCategory.categorySelected', { code: selectedCategory })}
                        {(() => {
                          const selectedCat = supplyCategories.find(cat => cat.code === selectedCategory);
                          return selectedCat ? ` ${t('filters.supplyCategory.productsCount', { count: selectedCat.count })}` : '';
                        })()}
                      </span>
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplyCategoryFilter; 